<template>
    <div>
        <b-form-checkbox :checked="enabled" switch
            :disabled="!hasAcceptanceConfigPermission"
            v-b-tooltip.hover="!hasAcceptanceConfigPermission ? 'You do not have permission to create acceptance records.' : ''"
            @change="$emit('update:enabled', $event)">
            Create acceptance record when saving this document
        </b-form-checkbox>
        <b-collapse :visible="enabled && hasAcceptanceConfigPermission" class="mt-2">
            <b-form-group label="Select Acceptance Configuration:">
                <SearchComponent
                    :value="configuration"
                    search-u-r-l="/AcceptanceConfiguration"
                    :option-label="configLabel"
                    :prepopulate="true"
                    :results-modifier="filterActiveConfigs"
                    label=""
                    placeholder="Search for acceptance configuration..."
                    @input="$emit('update:configuration', $event)" />
            </b-form-group>
            <b-form-group label="Source Type:">
                <b-select :value="sourceType" :options="acceptanceSourceTypeOptions"
                    @input="$emit('update:sourceType', $event)" />
            </b-form-group>
        </b-collapse>
    </div>
</template>

<script lang="ts">
import { Vue, Component, Prop } from 'vue-property-decorator';
import SearchComponent from '@/components/SearchComponent.vue';
import { AcceptanceConfiguration, AcceptanceConfigurationStatus } from '@/models/AcceptanceConfiguration';
import { AcceptanceSource, getSourceTypeOptionsWithPlaceholder } from '@/models/AcceptanceSource';

/**
 * Shared acceptance-record selector: the "create acceptance record on save" toggle, the
 * active-configuration search, and the source-type select, with the permission gating that
 * governs them. Controlled component — the parent owns the three pieces of state and binds
 * them with .sync (:enabled.sync, :configuration.sync, :source-type.sync). Used by both the
 * patient documents form and the fax-split (PDF chopper) modal so the permission list and the
 * active-config rule live in one place.
 */
@Component({
    name: "AcceptanceConfigSelector",
    components: { SearchComponent },
})
export default class AcceptanceConfigSelector extends Vue {
    @Prop({ default: false, type: Boolean }) enabled!: boolean;
    @Prop({ default: null }) configuration!: AcceptanceConfiguration | null;
    @Prop({ default: null }) sourceType!: AcceptanceSource | null;

    get hasAcceptanceConfigPermission(): boolean {
        const acceptanceConfigPermissions = [
            "Maintenance/Acceptance Configuration",
            "Maintenance/Acceptance Configuration/Read",
            "Admin/FRM Web Management",
        ];
        return this.$permissions.some(p =>
            acceptanceConfigPermissions.includes(p.permissionCodeName)
        );
    }

    get acceptanceSourceTypeOptions() {
        return getSourceTypeOptionsWithPlaceholder();
    }

    protected configLabel(config: AcceptanceConfiguration): string {
        return config.name || '';
    }

    protected filterActiveConfigs(configs: AcceptanceConfiguration[]): AcceptanceConfiguration[] {
        return configs.filter(c => c.status === AcceptanceConfigurationStatus.Active);
    }
}
</script>
